import { Mn as Avatar$1, T as CssBaseline, a as keyframes, kn as Chip$1, n as alpha, on as Typography, s as styled, t as ThemeProvider$1, vt as Box } from "./mui-CuhS6TY5.js";
import { t as StyledSvgIcon } from "./SvgIcon.styles-D2P4wvY9.js";
import { createJotPsychTheme, jotpsychTheme } from "./theme/index.js";
import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";

//#region src/utils/sx.ts
function isSxArray(sx) {
	return Array.isArray(sx);
}
/**
* Normalize an `sx` prop into an array, so a component's own styles and the
* consumer's can be composed without either clobbering the other.
*/
function toSxArray(sx) {
	if (sx === void 0) return [];
	return isSxArray(sx) ? [...sx] : [sx];
}

//#endregion
//#region src/primitives/Avatar/Avatar.styles.ts
/**
* Styled Avatar component with theme integration
* Uses MUI's native sizing system
*/
const StyledAvatar = styled(Avatar$1)(() => ({
	containerType: "inline-size",
	fontWeight: 500,
	"& .MuiAvatar-fallback": {
		width: "100%",
		height: "100%"
	}
}));
/**
* Wrapper for initials text that responds to container size
*/
const InitialsWrapper = styled("span")({ fontSize: "38cqw" });

//#endregion
//#region src/primitives/Avatar/Avatar.utils.ts
/**
* Avatar utility functions and constants
*/
/**
* Color palette for avatars - 24 distinct dark/saturated colors
* Optimized for visibility as text color on 15% opacity backgrounds
* Colors are grouped by hue family but distributed for maximum distinction
*/
const AVATAR_COLORS = [
	"#1A237E",
	"#283593",
	"#4A148C",
	"#5B2C6F",
	"#6A1B9A",
	"#7B1FA2",
	"#B71C1C",
	"#C2185B",
	"#AD1457",
	"#880E4F",
	"#1B5E20",
	"#2E7D32",
	"#004D40",
	"#00695C",
	"#006064",
	"#01579B",
	"#0D47A1",
	"#1565C0",
	"#E65100",
	"#BF360C",
	"#D84315",
	"#FF6F00",
	"#F57C00",
	"#FF8F00"
];
/**
* Generate a deterministic color based on an ID
* Same ID will always produce the same color
* Uses FNV-1a hash algorithm for better distribution with UUIDs
* @param id - Unique identifier
* @returns Hex color string from the palette
*/
const getAvatarColor = (id) => {
	if (!id || typeof id !== "string") return AVATAR_COLORS[0];
	const cleanId = id.replace(/-/g, "").toLowerCase();
	let hash = 2166136261;
	for (let i = 0; i < cleanId.length; i++) {
		hash ^= cleanId.charCodeAt(i);
		hash = Math.imul(hash, 16777619);
	}
	hash = (hash << 13 | hash >>> 19) >>> 0;
	hash = Math.imul(hash, 1540483477);
	hash = (hash << 15 | hash >>> 17) >>> 0;
	return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};
/**
* Extract initials from first and last names
* @param firstName - Patient's first name
* @param lastName - Patient's last name
* @returns Uppercase initials or "?" if no names provided
*/
const getInitials = (firstName, lastName) => {
	return `${firstName?.trim()?.charAt(0)?.toUpperCase() || ""}${lastName?.trim()?.charAt(0)?.toUpperCase() || ""}` || "?";
};
/**
* Convert hex color to RGBA with specified opacity
* @param hex - Hex color string (with or without #)
* @param opacity - Opacity value between 0 and 1
* @returns RGBA color string
*/
const hexToRgba = (hex, opacity) => {
	const cleanHex = hex.replace("#", "");
	return `rgba(${parseInt(cleanHex.substring(0, 2), 16)}, ${parseInt(cleanHex.substring(2, 4), 16)}, ${parseInt(cleanHex.substring(4, 6), 16)}, ${opacity})`;
};

//#endregion
//#region src/primitives/Avatar/Avatar.tsx
/**
* Primitive Avatar component with deterministic colors
* Always renders as circular variant with consistent styling
*/
const Avatar = ({ id, firstName, lastName, src, children, sx, ...props }) => {
	const initials = !src && !children ? getInitials(firstName, lastName) : void 0;
	const avatarColor = id && !src ? getAvatarColor(id) : void 0;
	return /* @__PURE__ */ jsx(StyledAvatar, {
		variant: "circular",
		src,
		sx: [...avatarColor ? [{
			color: avatarColor,
			backgroundColor: hexToRgba(avatarColor, .15)
		}] : [], ...toSxArray(sx)],
		...props,
		children: children || (initials ? /* @__PURE__ */ jsx(InitialsWrapper, { children: initials }) : void 0)
	});
};

//#endregion
//#region src/primitives/BrandLoader/BrandLoader.tsx
const SIZE_MAP = {
	sm: {
		square: 10,
		radius: 2.5,
		gap: 4,
		fontSize: 12
	},
	md: {
		square: 14,
		radius: 3,
		gap: 5,
		fontSize: 13
	},
	lg: {
		square: 18,
		radius: 3.5,
		gap: 6,
		fontSize: 14
	}
};
const COLORS = {
	light: {
		primary: "#1C1E85",
		secondary: "#FD96C9"
	},
	dark: {
		primary: "#FD96C9",
		secondary: "#FFF2F5"
	}
};
const gentleWave = keyframes`
  0%, 100% {
    transform: rotate(0deg) scale(1);
  }
  35% {
    transform: rotate(45deg) scale(0.82);
  }
  65% {
    transform: rotate(45deg) scale(0.82);
  }
  85% {
    transform: rotate(0deg) scale(1.06);
  }
`;
/**
* JotPsych branded loading indicator.
*
* Three colored squares rotate sequentially to 45° (diamond) and back,
* creating a gentle ripple effect.
*/
const BrandLoader = ({ variant = "light", size = "md", label, className, sx }) => {
	const dims = SIZE_MAP[size];
	const colors = COLORS[variant];
	const squareBaseSx = {
		width: dims.square,
		height: dims.square,
		borderRadius: `${dims.radius}px`,
		animation: `${gentleWave} 2s ease-in-out infinite`,
		flexShrink: 0
	};
	const consumerSx = toSxArray(sx);
	return /* @__PURE__ */ jsxs(Box, {
		className,
		role: "status",
		"aria-label": label || "Loading",
		sx: [{
			display: "inline-flex",
			alignItems: "center",
			gap: label ? `${dims.gap + 4}px` : 0
		}, ...consumerSx],
		children: [/* @__PURE__ */ jsxs(Box, {
			sx: {
				display: "inline-flex",
				alignItems: "center",
				gap: `${dims.gap}px`
			},
			children: [
				/* @__PURE__ */ jsx(Box, { sx: [squareBaseSx, {
					bgcolor: colors.secondary,
					animationDelay: "0s"
				}] }),
				/* @__PURE__ */ jsx(Box, { sx: [squareBaseSx, {
					bgcolor: colors.primary,
					animationDelay: "0.2s"
				}] }),
				/* @__PURE__ */ jsx(Box, { sx: [squareBaseSx, {
					bgcolor: colors.secondary,
					animationDelay: "0.4s"
				}] })
			]
		}), label && /* @__PURE__ */ jsx(Typography, {
			component: "span",
			sx: {
				fontSize: dims.fontSize,
				fontWeight: 500,
				color: variant === "dark" ? "rgba(255,255,255,0.6)" : "textColors.lightText",
				whiteSpace: "nowrap",
				userSelect: "none"
			},
			children: label
		})]
	});
};

//#endregion
//#region src/primitives/Chip/Chip.tsx
const defaultChipProps = {
	color: "default",
	size: "small",
	variant: "soft"
};
/**
* JotPsych Chip primitive.
*
* Defaults to the design-system status/tag treatment while still allowing
* MUI's standard chip variants as an escape hatch.
*/
const Chip = React.forwardRef(({ color, size, variant, ...props }, ref) => /* @__PURE__ */ jsx(Chip$1, {
	ref,
	...props,
	color: color ?? defaultChipProps.color,
	size: size ?? defaultChipProps.size,
	variant: variant ?? defaultChipProps.variant
}));
Chip.displayName = "Chip";

//#endregion
//#region src/primitives/CompoundChip/CompoundChip.styles.ts
/**
* Pill-shaped container that wraps a primary Chip and an optional secondary element.
*/
const StyledContainer = styled(Box, { shouldForwardProp: (prop) => prop !== "containerColor" })(({ theme, containerColor }) => ({
	display: "inline-flex",
	alignItems: "center",
	gap: theme.spacing(.5),
	borderRadius: "16px",
	backgroundColor: containerColor ?? theme.palette.action.selected,
	"& .MuiChip-root": { minWidth: 74 },
	"& .CompoundChip-secondary": {
		display: "inline-flex",
		alignItems: "center",
		"& .MuiSvgIcon-root": {
			fontSize: "0.875rem",
			color: theme.palette.text.secondary
		}
	},
	"& .CompoundChip-secondary-start": { marginLeft: theme.spacing(1) },
	"& .CompoundChip-secondary-end": { marginRight: theme.spacing(1) }
}));

//#endregion
//#region src/primitives/CompoundChip/CompoundChip.tsx
/**
* Compound chip primitive - a primary Chip with an optional secondary element
* (icon, avatar, etc.) displayed alongside it inside a shared pill-shaped container.
*
* Standard JotPsych Chip props are forwarded to the inner Chip.
*
* @example
* <CompoundChip
*   label="Published"
*   color="success"
*   icon={<PublishedIcon />}
*   secondary={<EditIcon />}
* />
*/
const CompoundChip = forwardRef(({ secondary, secondaryPlacement = "end", containerColor, containerSx, ...chipProps }, ref) => {
	if (!secondary) return /* @__PURE__ */ jsx(Chip, {
		ref,
		...chipProps
	});
	return /* @__PURE__ */ jsxs(StyledContainer, {
		ref,
		containerColor,
		sx: containerSx,
		children: [
			secondaryPlacement === "start" && /* @__PURE__ */ jsx(Box, {
				className: "CompoundChip-secondary CompoundChip-secondary-start",
				children: secondary
			}),
			/* @__PURE__ */ jsx(Chip, { ...chipProps }),
			secondaryPlacement === "end" && /* @__PURE__ */ jsx(Box, {
				className: "CompoundChip-secondary CompoundChip-secondary-end",
				children: secondary
			})
		]
	});
});
CompoundChip.displayName = "CompoundChip";

//#endregion
//#region src/primitives/Mono/Mono.tsx
/**
* JotPsych Mono primitive.
*
* An inline span for codes and aligned figures (CPT, ICD-10, NPI, member IDs,
* CARC, X12 refs, money columns) — applies the theme's mono font stack and
* `tabular-nums` so digit columns line up, while inheriting font size and
* color from its surrounding context (`variant="inherit"` by default).
*/
const Mono = React.forwardRef(({ variant, component, sx, ...props }, ref) => /* @__PURE__ */ jsx(Typography, {
	ref,
	...props,
	variant: variant ?? "inherit",
	component: component ?? "span",
	sx: [(theme) => ({
		fontFamily: theme.typography.fontFamilyMono,
		fontVariantNumeric: "tabular-nums"
	}), ...Array.isArray(sx) ? sx : sx ? [sx] : []]
}));
Mono.displayName = "Mono";

//#endregion
//#region src/primitives/ProviderBadge/ProviderBadge.utils.ts
const PROVIDER_BADGE_COLORS = [
	"#6C00CC",
	"#D46D27",
	"#2563EB",
	"#059669",
	"#DB2777"
];
const normalizeProviderName = (name) => name?.trim().replace(/\s+/g, " ").toLowerCase() ?? "";
const getProviderBadgeColor = (name) => {
	const cleanName = normalizeProviderName(name);
	if (!cleanName) return PROVIDER_BADGE_COLORS[0];
	let hash = 2166136261;
	for (let index = 0; index < cleanName.length; index += 1) {
		hash ^= cleanName.charCodeAt(index);
		hash = Math.imul(hash, 16777619);
	}
	hash = (hash << 13 | hash >>> 19) >>> 0;
	hash = Math.imul(hash, 1540483477);
	hash = (hash << 15 | hash >>> 17) >>> 0;
	return PROVIDER_BADGE_COLORS[hash % PROVIDER_BADGE_COLORS.length];
};
const getProviderInitials = (name) => (name ?? "").replace(/^(Dr|Mr|Mrs|Ms|Mx)\.?\s+/i, "").split(/\s+/).filter(Boolean).map((word) => word[0]).join("").slice(0, 2).toUpperCase() || "--";

//#endregion
//#region src/primitives/ProviderBadge/ProviderBadge.tsx
const ProviderBadge = ({ name, ariaLabel, selected = true, size = "compact" }) => {
	const color = getProviderBadgeColor(name);
	const label = name ? ariaLabel ?? `Provider: ${name}` : ariaLabel ?? "Unassigned provider";
	const dims = size === "regular" ? {
		height: 24,
		minWidth: 30,
		fontSize: 11,
		px: .75
	} : {
		height: 18,
		minWidth: 24,
		fontSize: 10,
		px: .5
	};
	return /* @__PURE__ */ jsx(Box, {
		component: "span",
		"aria-label": label,
		sx: {
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			height: dims.height,
			minWidth: dims.minWidth,
			px: dims.px,
			borderRadius: "4px",
			bgcolor: name && selected ? color : "transparent",
			color: name ? selected ? "#fff" : color : "#9BA1B7",
			border: name && selected ? "none" : `1.5px solid ${name ? color : "#C6CAD8"}`,
			fontSize: dims.fontSize,
			fontWeight: 700,
			lineHeight: 1,
			flexShrink: 0
		},
		children: getProviderInitials(name)
	});
};

//#endregion
//#region src/primitives/ScrollFade/ScrollFade.tsx
const FADE_HEIGHT = 48;
const SCROLL_THRESHOLD = 20;
const SCROLLBAR_WIDTH = 6;
/**
* Wraps scrollable content with a bottom-fade gradient that signals more content below.
* The gradient hides once the user has scrolled to within ~20px of the bottom.
*
* Intended for long modals where the natural overflow cutoff would otherwise hide the
* fact that there's more content past the fold. The wrapper expects to live inside a
* flex parent — it stretches to fill available space and scrolls internally.
*/
const ScrollFade = React.forwardRef(({ children }, ref) => {
	const scrollRef = useRef(null);
	const [showFade, setShowFade] = useState(false);
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		const check = () => {
			const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
			setShowFade(el.scrollHeight > el.clientHeight && distanceFromBottom >= SCROLL_THRESHOLD);
		};
		check();
		el.addEventListener("scroll", check, { passive: true });
		const observer = new ResizeObserver(check);
		observer.observe(el);
		return () => {
			el.removeEventListener("scroll", check);
			observer.disconnect();
		};
	}, []);
	return /* @__PURE__ */ jsxs(Box, {
		ref,
		sx: {
			position: "relative",
			overflow: "hidden",
			flex: 1,
			minHeight: 0,
			display: "flex",
			flexDirection: "column"
		},
		children: [/* @__PURE__ */ jsx(Box, {
			ref: scrollRef,
			sx: (theme) => ({
				overflow: "auto",
				flex: 1,
				minHeight: 0,
				"&::-webkit-scrollbar": { width: SCROLLBAR_WIDTH },
				"&::-webkit-scrollbar-thumb": {
					backgroundColor: alpha(theme.palette.text.primary, .2),
					borderRadius: SCROLLBAR_WIDTH / 2
				}
			}),
			children
		}), showFade && /* @__PURE__ */ jsx(Box, { sx: (theme) => ({
			position: "absolute",
			bottom: 0,
			left: 0,
			right: 0,
			height: FADE_HEIGHT,
			background: `linear-gradient(transparent, ${alpha(theme.palette.background.paper, .95)})`,
			pointerEvents: "none"
		}) })]
	});
});
ScrollFade.displayName = "ScrollFade";

//#endregion
//#region src/theme/ThemeProvider.tsx
const ThemeProvider = ({ children, extendTheme, disableCssBaseline = false }) => {
	return /* @__PURE__ */ jsxs(ThemeProvider$1, {
		theme: useMemo(() => extendTheme ? createJotPsychTheme(extendTheme) : jotpsychTheme, [extendTheme]),
		children: [!disableCssBaseline && /* @__PURE__ */ jsx(CssBaseline, {}), children]
	});
};

//#endregion
export { AVATAR_COLORS, Avatar, BrandLoader, Chip, CompoundChip, Mono, PROVIDER_BADGE_COLORS, ProviderBadge, ScrollFade, StyledSvgIcon as SvgIcon, ThemeProvider, createJotPsychTheme, getAvatarColor, getInitials, getProviderBadgeColor, getProviderInitials, hexToRgba, jotpsychTheme };
//# sourceMappingURL=index.js.map